import {
  CACHE_ENABLED,
  CACHE_GC_TIME,
  CACHE_STALE_TIME,
} from "src/config/cache.config";
import { queryClient } from "src/queryClient";

/**
 * Wraps an API function with TanStack Query cache.
 *
 * The returned function has the **same signature** as the original,
 * so it's a drop-in replacement in API_SERVICES. All calls go through
 * `queryClient.fetchQuery` with env-configurable staleTime by default.
 *
 * When REACT_APP_CACHE_ENABLED is "false", the cache is completely
 * bypassed and the raw API function is called directly.
 *
 * Additionally, `.remove()` and `.invalidate()` methods are attached
 * for cache management from any consumer.
 *
 * @param apiFn     - Original API function
 * @param cacheKey  - Unique TanStack Query cache key
 * @param onSuccess - Optional callback invoked after a successful fetch
 * @param staleTime - Optional cache time in milliseconds
 *
 * @example
 * ```ts
 * // In services/index.ts — drop-in replacement:
 * GET_OFFICE_USERS: withCache(getOfficeUsers, "offices-list"),
 *
 * // Consumers call it the same way:
 * API_SERVICES.DENTAL_OFFICE.GET_OFFICE_USERS(params);
 *
 * // Cache management:
 * API_SERVICES.DENTAL_OFFICE.GET_OFFICE_USERS.remove();
 * API_SERVICES.DENTAL_OFFICE.GET_OFFICE_USERS.invalidate();
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withCache = <TArgs extends any[], TResult>(
  apiFn: (...args: TArgs) => Promise<TResult>,
  cacheKey: string,
  onSuccess?: () => void,
  staleTime?: number,
  gcTime?: number
) => {
  // Fallback chain: per-query arg → env config → hardcoded default
  const DEFAULT_STALE_TIME = 1000 * 60 * 2; // 2 minutes
  const DEFAULT_GC_TIME = 1000 * 60 * 5; // 5 minutes
  const STALE_TIME = staleTime || CACHE_STALE_TIME || DEFAULT_STALE_TIME;
  const GC_TIME = gcTime || CACHE_GC_TIME || DEFAULT_GC_TIME;

  const wrappedFn = (...args: TArgs): Promise<TResult> => {
    // When caching is disabled, bypass fetchQuery entirely
    if (!CACHE_ENABLED) {
      const result = apiFn(...args);
      result.then(() => onSuccess?.());
      return result;
    }

    return queryClient.fetchQuery({
      queryKey: [cacheKey, ...args],
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      queryFn: async () => {
        const result = await apiFn(...args);
        onSuccess?.();
        return result;
      },
    });
  };

  return Object.assign(wrappedFn, {
    /** Remove all cached entries for this query key */
    remove: () => queryClient.removeQueries({ queryKey: [cacheKey] }),
    /** Invalidate cache — triggers background refetch for active queries */
    invalidate: () => queryClient.invalidateQueries({ queryKey: [cacheKey] }),
    /** The raw cache key, for advanced usage */
    cacheKey,
  });
};

/**
 * Wraps a **mutation** API function so that on success (response.ok),
 * it automatically removes the specified cache keys.
 *
 * The returned function has the **same signature** as the original,
 * so it's a drop-in replacement in API_SERVICES.
 *
 * @param mutationFn     - Original mutation API function (POST/PUT/PATCH/DELETE)
 * @param cacheKeys      - Array of cache keys to remove on success
 *
 * @example
 * ```ts
 * // In services/index.ts — drop-in replacement:
 * OFFICE_REJECT_APPROVE: withRemoveCache(editOfficeApproveReject, ["offices-list"]),
 * DELETE_ACCOUNT: withRemoveCache(deleteOfficeAccount, ["offices-list"]),
 *
 * // Consumers call it the same way — cache cleanup is automatic:
 * API_SERVICES.DENTAL_OFFICE.OFFICE_REJECT_APPROVE(params, data);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withRemoveCache = <TArgs extends any[], TResult>(
  mutationFn: (...args: TArgs) => Promise<TResult>,
  cacheKeys: string[]
) => {
  const wrappedFn = async (...args: TArgs): Promise<TResult> => {
    const result = await mutationFn(...args);

    // Remove caches if the mutation was successful
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((result as any)?.ok) {
      cacheKeys.forEach(key => queryClient.removeQueries({ queryKey: [key] }));
    }

    return result;
  };

  return wrappedFn;
};
